import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { combineLatest } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { FiltersFacade } from '@modules/filters/facades';
import {
  defaultBuildingFormValue,
  intervalDay,
} from '@modules/filters/constants';
import { FilterTypeEnum } from '@modules/filters/enums';
import { BuildingsFacade } from '@modules/buildings/facades/buildings-facade.service';
import { datePickerDateFormat } from '@shared/constants';
import { LabelPosition, Size } from '@usitsdasdesign/dds-ng/shared';
import {
  SelectItemOptions,
  SelectOptions,
} from '@usitsdasdesign/dds-ng/select';
import { ModalService } from '@core/services';

@Component({
  standalone: false,
  selector: 'hm-filters-panel-building',
  templateUrl: './filters-panel-building.component.html',
  styleUrls: ['./filters-panel-building.component.scss'],
})
export class FiltersPanelBuildingComponent implements OnInit, AfterViewInit {
  private readonly buildingsFacade = inject(BuildingsFacade);
  private readonly filtersFacade = inject(FiltersFacade);
  private readonly modalService = inject(ModalService);
  public isDateRangeDisabled = true;
  public readonly vm$ = combineLatest([
    this.buildingsFacade.buildingsData$,
    this.filtersFacade.getFilterFormValueByType(
      FilterTypeEnum.BuildingUtilization,
    ),
  ]).pipe(
    map(([buildingsData, filterValue]) => ({
      buildingsData,
      filterValue,
    })),
  );
  public readonly datePickerDateFormat = datePickerDateFormat;
  public readonly today = new Date();
  public readonly defaultFormValue = defaultBuildingFormValue;
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  protected hoursTypesOptions: SelectItemOptions[] = [];

  selectOptions: SelectOptions = {
    labelPosition: LabelPosition.external,
    size: Size.md,
    customClass: 'add-building__form-building-type',
  };

  form: FormGroup = new FormGroup({
    building: new FormControl(null),
    dateRange: new FormControl(null),
    hoursType: new FormControl(null),
    device: new FormControl(null),
  });

  ngAfterViewInit(): void {
    if (!this.form.get('building')?.value) {
      this.form.get('dateRange')?.disable();
    }
  }

  ngOnInit(): void {
    this.form.get('building')?.valueChanges.subscribe((building) => {
      if (building) {
        this.form.get('dateRange')?.enable();
      } else {
        this.form.get('dateRange')?.disable();
      }
    });
    this.vm$
      .pipe(
        filter((vm) => !!vm.buildingsData && !!vm.filterValue),
        take(1),
      )
      .subscribe((vm) => {
        this.hoursTypesOptions = vm.buildingsData.hoursTypes.map((type) => ({
          heading: type.label,
          value: type.value,
          customClass: this.selectOptions.customClass,
          active: type.value === vm.filterValue.hoursType?.value,
        }));

        this.form.patchValue(vm.filterValue);
        this.form
          .get('hoursType')
          ?.setValue(vm.filterValue.hoursType.value, { emitEvent: true });
        Promise.resolve().then(() => {
          this.form.get('dateRange')?.setValue(vm.filterValue.dateRange);
        });
        this.changeDetectorRef.markForCheck();
      });
  }

  selectBuilding(): void {
    this.form.get('dateRange')?.reset();
    this.form.get('dateRange')?.setErrors(null);
    this.resetHoursType();
  }

  selectDateRange(): void {
    this.resetHoursType();
  }
  submit(): void {
    const dateRange = this.form.get('dateRange');
    dateRange?.setValidators([Validators.required]);
    dateRange?.updateValueAndValidity();
    if (this.form.invalid) {
      return;
    }
    const filter = this.filtersFacade.convertFormValueToModel(
      FilterTypeEnum.BuildingUtilization,
      {
        ...this.form.value,
        interval: intervalDay,
      },
    );
    this.filtersFacade.applyFilter(filter);
    this.modalService.close();
  }

  private resetHoursType(): void {
    const defaultValue = defaultBuildingFormValue.hoursType.value;

    this.hoursTypesOptions = this.hoursTypesOptions.map((option) => ({
      ...option,
      active: option.value === defaultValue,
    }));

    this.form.get('hoursType')?.setValue(defaultValue, { emitEvent: false });
  }
}
